"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Newspaper,
  Calendar,
  ArrowLeft,
  User,
  Eye,
  Tag,
  Pencil,
  Music2,
  Disc3,
  Video,
  ShoppingBag,
  CalendarDays,
  ImageIcon,
  ExternalLink,
  Globe,
  Play,
  Pause,
} from "lucide-react";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { ShareButton } from "@/components/ui/share-button";
import { QRShareButton } from "@/components/ui/qr-share-button";
import SaveButton from "@/components/save-button";
import NewsEngagement from "@/components/news/news-engagement";
import React, { useState, useEffect, use } from "react";
import dynamic from "next/dynamic";
import { getNewsBySlug, getNewsArticles } from "@/services/news";
import { useAdmin } from "@/hooks/use-admin";
import { sanitizeHtml, plainTextToHtml, embedFacebook, embedYouTube, styleSourceLinks } from "@/lib/sanitize";
import { hasRealNewsThumb, newsHref } from "@/lib/news-image";
import { generateInitialsSvgDataUrl } from "@/lib/thumbnail-utils";
import type { NewsArticle } from "@/types/news";
import { trackMediaEvent } from "@/services/stats";
import { NewsArticleLD } from "@/components/seo/json-ld";
import { useTranslations } from "next-intl";
import { getCoverIndex } from "@/services/cover-index";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { playableTracksFor } from "@/lib/playable-tracks";
import { useToast } from "@/hooks/use-toast";

const NewsEditorDialog = dynamic(() => import("@/components/admin/news-editor-dialog"), { ssr: false });

const categoryColors: Record<string, string> = {
  news: "bg-blue-600 text-white",
  press: "bg-purple-600 text-white",
  release: "bg-green-600 text-white",
  interview: "bg-amber-500 text-black",
  feature: "bg-pink-600 text-white",
  announcement: "bg-red-600 text-white",
};

export default function NewsArticlePage({ params }: { params: Promise<{ slug: string }> }) {
  const resolvedParams = use(params);
  const [article, setArticle] = useState<NewsArticle | null>(null);
  // Featured Content art: a song/release picked without its own artwork shows
  // its release cover → artist photo (the "once and for all" chain) instead
  // of a music-note placeholder (Gordon 2026-10-31).
  const [relatedArt, setRelatedArt] = useState<Record<string, string>>({});
  useEffect(() => {
    const rc = (article as any)?.relatedContent as any[] | undefined;
    if (!rc?.some(it => it && (it.type === 'work' || it.type === 'release'))) return;
    let alive = true;
    getCoverIndex().then(ix => {
      if (!alive) return;
      const m: Record<string, string> = {};
      rc.forEach(it => {
        if (it?.type !== 'work' && it?.type !== 'release') return;
        const art = ix.forWork({ id: it.id, workId: it.id, title: it.title, artistName: it.subtitle, coverArtUrl: it.imageUrl });
        if (art) m[it.id] = art;
      });
      setRelatedArt(m);
    }).catch(() => {});
    return () => { alive = false; };
  }, [article]);
  const [relatedArticles, setRelatedArticles] = useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [userLocale, setUserLocale] = useState<string>("en");
  const [showTranslation, setShowTranslation] = useState(true);
  const { isAdmin } = useAdmin();
  const t = useTranslations("news");
  const tCommon = useTranslations("common");
  const tHome = useTranslations("home");
  const { toast } = useToast();

  // Featured songs/releases play like Home's and the Dashboard's cards
  // (Gordon 2026-11-03: "missing play buttons like on home and dashboard"):
  // the button plays the content, or pauses/resumes it when it's playing.
  const { playTrack, playTracks, currentTrack, isPlaying, togglePlay } = useMediaPlayer();
  const [playingFrom, setPlayingFrom] = useState<{ cardId: string; trackIds: string[] } | null>(null);
  const isCardCurrent = (cardId: string) =>
    !!currentTrack && (currentTrack.id === cardId || (playingFrom?.cardId === cardId && playingFrom.trackIds.includes(currentTrack.id)));
  const playFeatured = async (item: { id: string; title: string; subtitle?: string; imageUrl?: string }) => {
    if (isCardCurrent(item.id)) { togglePlay(); return; }
    try {
      const tracks = await playableTracksFor({ id: item.id, title: item.title, artistName: item.subtitle, imageUrl: relatedArt[item.id] || item.imageUrl });
      if (tracks.length === 0) { toast({ title: tHome("nothingToPlay") }); return; }
      setPlayingFrom({ cardId: item.id, trackIds: tracks.map(tr => tr.id) });
      if (tracks.length === 1) playTrack(tracks[0]); else playTracks(tracks, 0);
    } catch {
      toast({ title: tHome("nothingToPlay") });
    }
  };

  // Deep-link: /news/<slug>?edit=1 opens the WYSIWYG editor straight away —
  // used by the Edit button on search results.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("edit") === "1" && isAdmin && article) {
      setEditorOpen(true);
    }
  }, [isAdmin, article]);

  // Detect browser locale
  useEffect(() => {
    const lang = navigator.language || "en";
    const code = lang.includes("-") && lang.toLowerCase() !== "zh-tw"
      ? lang.split("-")[0]
      : lang;
    setUserLocale(code.toLowerCase());
  }, []);

  useEffect(() => {
    async function loadArticle() {
      try {
        const data = await getNewsBySlug(resolvedParams.slug);
        setArticle(data);
        if (data) {
          trackMediaEvent({
            type: 'view',
            entityType: 'news',
            entityId: data.id || resolvedParams.slug,
            entityTitle: data.title,
          });
        }

        // Load related articles — SAME TOPIC first (sub-category, category,
        // tag overlap), never hidden/deleted/unpublished, and never articles
        // without a real thumbnail (junk ™/logo images count as none).
        if (data) {
          const all = await getNewsArticles(80);
          const tagsOf = (a: any) => new Set<string>((Array.isArray(a.tags) ? a.tags : []).map((t: any) => String(t).toLowerCase()));
          const myTags = tagsOf(data);
          const score = (a: any) => {
            let s = 0;
            if ((a as any).subCategory && (a as any).subCategory === (data as any).subCategory) s += 4;
            if ((a as any).category === (data as any).category) s += 2;
            tagsOf(a).forEach((t: string) => { if (myTags.has(t)) s += 1; });
            return s;
          };
          const related = all
            .filter((a) => a.id !== data.id)
            .filter((a: any) => !a.adminHidden && !a.deleted && (!a.status || a.status === 'published'))
            .filter((a: any) => hasRealNewsThumb(a.imageUrl || a.featuredImageUrl || a.thumbnailUrl) || !!(a as any).videoUrl)
            .map((a) => ({ a, s: score(a) }))
            .sort((x, y) => y.s - x.s)
            .slice(0, 8)
            .map((x) => x.a);
          setRelatedArticles(related);
        }
      } catch (err) {
        console.error("Error loading article:", err);
      } finally {
        setIsLoading(false);
      }
    }
    loadArticle();
  }, [resolvedParams.slug]);

  const reloadArticle = async () => {
    const data = await getNewsBySlug(resolvedParams.slug);
    setArticle(data);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Resolved translation for current locale
  const articleTranslations = (article as any)?.translations || {};
  const articleLocale = articleTranslations[userLocale];
  const hasTranslation = !!(articleLocale?.title) && userLocale !== "en";
  const displayTitle   = (hasTranslation && showTranslation) ? (articleLocale.title   || article?.title)   : article?.title;
  const displayExcerpt = (hasTranslation && showTranslation) ? (articleLocale.excerpt || article?.excerpt) : article?.excerpt;
  const displayContent = (hasTranslation && showTranslation && articleLocale?.bodyText)
    ? articleLocale.bodyText
    : (article?.content || "");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Newspaper className="h-12 w-12 text-muted-foreground animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">{t("loadingArticle")}</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Newspaper className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">{t("notFound")}</h2>
          <p className="text-muted-foreground mb-4">
            {t("notFoundDesc")}
          </p>
          <Link href="/news">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("backToNews")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // One card, two homes: the desktop right-hand rail and the mobile/tablet
  // bottom grid render the identical markup.
  const renderRelatedCard = (related: NewsArticle) => (
    <Link key={related.id} href={newsHref(related)} className="group block">
      <div className="bg-card rounded-lg overflow-hidden border border-border/50 hover:border-primary/30 transition-colors">
        <div className="relative w-full aspect-square bg-muted">
          {((related as any).imageUrl || (related as any).thumbnailUrl || (related as any).coverArtUrl || (related as any).thumbnail || related.featuredImageUrl) ? (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={(related as any).imageUrl || (related as any).thumbnailUrl || (related as any).coverArtUrl || (related as any).thumbnail || related.featuredImageUrl || ""}
              alt={related.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:opacity-90 transition-opacity duration-300"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Newspaper className="h-8 w-8 text-muted-foreground/20" />
            </div>
          )}
        </div>
        <div className="p-3">
          <h3 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
            {related.title}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {formatDate(related.publishedAt)}
          </p>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NewsArticleLD
        headline={article.title}
        description={article.excerpt}
        imageUrl={(article as any).imageUrl || (article as any).thumbnailUrl || article.featuredImageUrl}
        url={`${process.env.NEXT_PUBLIC_APP_URL || 'https://breakinbread.com'}/news/${resolvedParams.slug}`}
        publishedAt={(article.publishedAt as any)?.toDate ? (article.publishedAt as any).toDate().toISOString() : (article.publishedAt as any)}
        authorName={(article as any).author?.displayName ?? (typeof article.author === 'string' ? article.author : undefined)}
      />
      {/* Header */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-[50px] z-30">
        {/* Phones: the last button sits centred under the header's corner
            button (the menu; search when the menu is on the left) — a
            header-sized square flush with the edge, so the 40px button gets
            half the difference as right padding (Gordon 2026-11-02). From sm
            up the menu is the sidebar, which this bar stops short of. */}
        <div className="w-full px-page py-3 max-sm:pr-[calc((var(--header-height)_-_2.5rem)_*_0.5)]">
          <div className="flex items-center justify-between">
            <Breadcrumbs className="min-w-0 flex-1" items={[
              { navKey: 'news', href: '/news', icon: Newspaper },
              ...(article?.title ? [{ label: article.title, icon: Newspaper }] : []),
            ]} />
            {/* One size + one outline for every header button (Gordon
                2026-11-02: "the 3 buttons … need to be the same size"). */}
            <div className="flex shrink-0 items-center gap-1.5">
              {isAdmin && article && (
                <Button variant="outline" size="icon" onClick={() => setEditorOpen(true)} aria-label={tCommon('edit')}>
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <SaveButton
                itemId={article?.id || resolvedParams.slug}
                itemType="news"
                title={article?.title || ''}
                subtitle={article?.category || 'News'}
                imageUrl={(article as any)?.imageUrl || (article as any)?.thumbnailUrl || article?.featuredImageUrl || ''}
                link={`/news/${article?.slug || resolvedParams.slug}`}
                size="icon"
                showLabel={false}
              />
              <ShareButton
                title={article?.title}
                variant="outline"
                iconOnly
                size="icon"
                className="border-btn-outline-border"
                onShare={() => article && trackMediaEvent({
                  type: 'share',
                  entityType: 'news',
                  entityId: article.id || resolvedParams.slug,
                  entityTitle: article.title,
                })}
              />
              <QRShareButton size="icon" title={article?.title || "Article"} imageUrl={(article as any)?.imageUrl || (article as any)?.thumbnailUrl || article?.featuredImageUrl || ""} />
            </div>
          </div>
        </div>
      </div>

      {/* Article + desktop "More articles" rail. On lg+ the page becomes a
          two-column grid: a READING-width article column (Gordon: full-width
          articles were too wide to read, hero too large) and a sticky,
          INDEPENDENTLY scrolling vertical rail of related articles. */}
      <div className="w-full px-page py-8 lg:grid lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_340px] lg:gap-10 lg:items-start">
      <article className="w-full min-w-0 max-w-3xl mx-auto lg:mx-0">
        {/* Featured Image — natural aspect ratio, adapts to real image height.
            Capped so the hero never dominates the reading column. Falls back
            to a generated initials SVG so external scraped articles without
            their own thumbnail still get a hero card. */}
        {(() => {
          const realImg = (article as any).imageUrl
            || (article as any).thumbnailUrl
            || (article as any).coverArtUrl
            || (article as any).thumbnail
            || article.featuredImageUrl;
          const heroSrc = realImg || generateInitialsSvgDataUrl(article.title || 'News', 1200);
          return (
            <div className="w-full mb-8">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={heroSrc}
                alt={article.title}
                style={{ width: "100%", height: realImg ? "auto" : "320px", maxHeight: 420, display: "block", objectFit: "cover" }}
                className="rounded-xl"
              />
            </div>
          );
        })()}

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {article.category && (
            <Badge
              className={`capitalize ${
                categoryColors[article.category] || "bg-secondary text-secondary-foreground"
              }`}
            >
              {article.category}
            </Badge>
          )}
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(article.publishedAt)}
          </span>
          {((article as any).author?.displayName || typeof article.author === 'string') && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              {(article as any).author?.displayName ?? article.author as string}
            </span>
          )}
          {article.viewCount !== undefined && article.viewCount > 0 && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Eye className="h-3.5 w-3.5" />
              {t("views", { n: article.viewCount.toLocaleString() })}
            </span>
          )}
        </div>

        {/* Translation badge */}
        {hasTranslation && (
          <div className="flex items-center gap-2 mb-4">
            <span className="flex items-center gap-1.5 text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              <Globe className="h-3 w-3" />
              Auto-translated
            </span>
            <button
              onClick={() => setShowTranslation(v => !v)}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            >
              {showTranslation ? "Show original" : "Show translation"}
            </button>
          </div>
        )}

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight">
          {displayTitle}
        </h1>

        {/* Excerpt */}
        {displayExcerpt && !displayContent?.replace(/<[^>]*>/g, '').trim().startsWith(displayExcerpt.trim()) && (
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            {displayExcerpt}
          </p>
        )}

        {/* BB Video Embed — from video vault */}
        {(article as any).bbVideo?.youtubeId && (
          <div className="mb-8 rounded-xl overflow-hidden bg-black aspect-video w-full">
            <iframe
              src={`https://www.youtube.com/embed/${(article as any).bbVideo.youtubeId}?rel=0&modestbranding=1`}
              title={(article as any).bbVideo.title || article.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0"
            />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-invert prose-lg max-w-none
            prose-headings:text-foreground
            prose-p:text-foreground/90 prose-p:leading-relaxed
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-lg
            prose-blockquote:border-primary/50 prose-blockquote:text-muted-foreground
            prose-strong:text-foreground
            prose-code:text-primary prose-code:bg-secondary/50 prose-code:rounded prose-code:px-1
            [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:border-0 [&_img]:mx-auto
          "
          dangerouslySetInnerHTML={{ __html: styleSourceLinks(sanitizeHtml(embedYouTube(embedFacebook(plainTextToHtml(displayContent))))) }}
        />

        {/* External URL CTA */}
        {(article as any).externalUrl && (
          <div className="mt-8 pt-6 border-t border-border/50">
            <a
              href={(article as any).externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold px-5 py-2.5 rounded-lg text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              {(article as any).externalUrlLabel || t("visitLink")}
            </a>
          </div>
        )}

        {/* Linked people — BB users this article is about / mentions */}
        {(article as any).relatedUsers?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-border/50">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> People in this story</p>
            <div className="flex flex-wrap gap-2">
              {(article as any).relatedUsers.map((u: any) => (
                <Link key={u.userId} href={`/users/${u.userId}`} className="inline-flex items-center gap-2 rounded-full border border-border/60 hover:border-primary/50 px-2.5 py-1 transition-colors">
                  {u.imageUrl
                    ? <img src={u.imageUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                    : <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{(u.displayName || '?').slice(0, 1)}</span>}
                  <span className="text-sm font-medium">{u.displayName}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-border/50">
            <Tag className="h-4 w-4 text-muted-foreground" />
            {article.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Related Artists */}
        {article.relatedArtists && article.relatedArtists.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{t("relatedArtists")}</h3>
            <div className="flex flex-wrap gap-2">
              {article.relatedArtists.map((artist) => (
                <Link
                  key={artist.artistId}
                  href={`/artists/${artist.artistId}`}
                  className="text-sm text-primary hover:underline"
                >
                  {artist.artistName}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related BB Content */}
        {(article as any).relatedContent && (article as any).relatedContent.length > 0 && (
          <div className="mt-10 pt-6 border-t border-border/50">
            <h3 className="text-base font-semibold mb-4">{t("featuredContent")}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {((article as any).relatedContent as Array<{id:string;type:string;title:string;subtitle?:string;imageUrl?:string;link:string}>).map((item) => {
                const TypeIcon = item.type === 'work' ? Music2
                  : item.type === 'release' ? Disc3
                  : item.type === 'video' ? Video
                  : item.type === 'marketplace' ? ShoppingBag
                  : item.type === 'event' ? CalendarDays
                  : ImageIcon;
                const playable = item.type === 'work' || item.type === 'release';
                const playing = playable && isCardCurrent(item.id) && isPlaying;
                return (
                  <div key={`${item.type}-${item.id}`} className="group relative">
                  <Link href={item.link} className="block">
                    <div className="bg-card border border-border/50 rounded-lg overflow-hidden hover:border-primary/30 transition-colors">
                      <div className="relative aspect-square bg-muted">
                        {(relatedArt[item.id] || item.imageUrl) ? (
                          <Image src={relatedArt[item.id] || item.imageUrl!} alt={item.title} fill unoptimized className="object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (item.type === 'work' || item.type === 'release') ? (
                          <GenerativeArtistAvatar name={item.title} size={256} className="absolute inset-0 w-full h-full" animate />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <TypeIcon className="h-8 w-8 text-muted-foreground/20" />
                          </div>
                        )}
                        <div className="absolute top-1.5 left-1.5">
                          <span className="flex items-center gap-0.5 bg-black/60 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full backdrop-blur-sm capitalize">
                            <TypeIcon className="h-2.5 w-2.5" />{item.type === 'work' ? t("song") : item.type}
                          </span>
                        </div>
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-medium line-clamp-1 group-hover:text-primary transition-colors">{item.title}</p>
                        {item.subtitle && <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">{item.subtitle}</p>}
                      </div>
                    </div>
                  </Link>
                  {/* Play: over the artwork's bottom-right corner, outside the
                      link (a button can't sit inside an <a>). */}
                  {playable && (
                    <div className="pointer-events-none absolute inset-x-px top-px aspect-square">
                      <Button
                        variant="ghost" size="icon"
                        className="bb-frost-play pointer-events-auto absolute bottom-2 right-2 z-[1] h-[26px] w-[26px] rounded-full text-white ring-1 ring-white/70 transition-colors hover:bg-white hover:text-black hover:ring-black/60"
                        aria-label={`${playing ? tCommon('pause') : tCommon('play')} ${item.title}`}
                        aria-pressed={playing}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); void playFeatured(item); }}
                      >{playing ? <Pause className="h-3.5 w-3.5 fill-current" /> : <Play className="h-3.5 w-3.5 fill-current" />}</Button>
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Source attribution — the "Read the full story" link is a high-contrast
            outline button whose text is the SOURCE TITLE (Gordon). */}
        {/* In-house articles imported from the old breakinbread.com site keep
            that URL as sourceUrl — it would only link back to ourselves
            ("Read the full story on Gordon Cyrus"), so no source box. */}
        {article.sourceUrl && !/^https?:\/\/(www\.)?breakinbread\.com\b/i.test(article.sourceUrl) && (() => {
          const sourceName = (article as any).author?.displayName
            || (typeof article.author === 'string' ? article.author : '')
            || String((article as any).source || '').replace(/^industry-rss:/, '')
            || 'the source';
          return (
            <div className="mt-8 rounded-xl border border-border bg-muted/30 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Source</p>
                <p className="font-semibold truncate">{sourceName}</p>
              </div>
              <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <Button variant="outline" className="gap-2 border-2 border-foreground/70 text-foreground font-semibold hover:bg-foreground hover:text-background">
                  <ExternalLink className="h-4 w-4" /> Read the full story on {sourceName} →
                </Button>
              </a>
            </div>
          );
        })()}

        {/* Like + comment */}
        <NewsEngagement articleId={article.id || resolvedParams.slug} />
      </article>

      {/* Desktop rail — sticky, scrolls INDEPENDENTLY of the article
          (own overflow-y within the viewport height). */}
      {relatedArticles.length > 0 && (
        <aside className="hidden lg:block sticky top-[calc(var(--header-height)_+_1rem)] max-h-[calc(100vh_-_var(--header-height)_-_2rem)] overflow-y-auto pr-1">
          <h2 className="text-base font-semibold mb-4">{t("moreArticles")}</h2>
          <div className="flex flex-col gap-4 pb-4">
            {relatedArticles.map(renderRelatedCard)}
          </div>
        </aside>
      )}
      </div>

      {/* Mobile / tablet: related articles below the article, as before. */}
      {relatedArticles.length > 0 && (
        <section className="lg:hidden border-t border-border/50 bg-secondary/20">
          <div className="w-full px-page py-10">
            <h2 className="text-xl font-semibold mb-6">{t("moreArticles")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.slice(0, 6).map(renderRelatedCard)}
            </div>
          </div>
        </section>
      )}

      {/* Admin Editor Dialog */}
      {isAdmin && article && (
        <NewsEditorDialog
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          article={article}
          onSaved={reloadArticle}
          onDeleted={() => window.location.href = '/news'}
        />
      )}
    </div>
  );
}
